/**
 * Keeping a screen awake and full screen. The projector PC and the court tablets must never dim
 * mid-round. Wake lock needs the page to be visible and is dropped when it is hidden, so it is
 * asked for again whenever the page comes back.
 */

import { useEffect, useState } from "react";

type WakeLockSentinel = { release(): Promise<void>; addEventListener(type: "release", fn: () => void): void };

export function useWakeLock(enabled = true): boolean {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    const nav = navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<WakeLockSentinel> } };
    if (!nav.wakeLock) return;
    let sentinel: WakeLockSentinel | null = null;
    let stopped = false;
    const ask = async (): Promise<void> => {
      if (stopped || document.visibilityState !== "visible") return;
      try {
        sentinel = await nav.wakeLock!.request("screen");
        setHeld(true);
        sentinel.addEventListener("release", () => setHeld(false));
      } catch {
        setHeld(false);
      }
    };
    void ask();
    const onVisible = (): void => {
      if (document.visibilityState === "visible") void ask();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisible);
      void sentinel?.release().catch(() => undefined);
    };
  }, [enabled]);
  return held;
}

export function isFullscreen(): boolean {
  return Boolean(document.fullscreenElement);
}

export async function toggleFullscreen(): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await document.documentElement.requestFullscreen();
  } catch {
    // not allowed here (an iframe, or no gesture); nothing to do
  }
}

/** Re-renders when full screen is entered or left. */
export function useFullscreen(): boolean {
  const [on, setOn] = useState(() => (typeof document !== "undefined" ? isFullscreen() : false));
  useEffect(() => {
    const update = (): void => setOn(isFullscreen());
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);
  return on;
}
